import { ReactComponent as TrainIcon } from "Assets/icons/menu/train.svg";
import { Box, Button, Fab, Stack, Typography } from "@mui/material";
import StyledDrawer, { StyledDrawerActions, StyledDrawerContainer, StyledDrawerTitle } from "Components/UI/StyledDrawer";
import { useCallback, useEffect, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { produce } from "immer";
import { useSiteinfo } from "../hooks/useSiteinfo";
import { useLocation } from "react-router-dom";
import { ApiSimpleSiteinfoLabel } from "@incendium/api";
import { useSelectedProject } from "Hooks/useSelectedProject";
import { useKeyPressEvent } from "react-use";
import { saveSiteinfo } from "features/analytics/services/analyticsLabelService";
import { useNotification } from "Hooks";

function SiteinfoEditor() {
  const { selectedClient, selectedProject } = useSelectedProject()
  const location = useLocation()
  const { fetched, siteinfos, setSiteinfos } = useSiteinfo()
  const [open, setOpen] = useState(false);
  const [localLabel, setLocalLabel] = useState<ApiSimpleSiteinfoLabel>({})
  const { showSuccessNotification, showErrorNotification } = useNotification()
  const [shiftPressed, setShiftPressed] = useState(false)
  useKeyPressEvent("Shift", (event) => {
    setShiftPressed(true)
  }, (event) => {
    setShiftPressed(false)
  });



  /**
   * cleanPath replaces client and project id with :clientId and :projectId
   */
  const cleanPath = useCallback((path: string) => {
    if (selectedClient && selectedProject) {
      path = path.replace(`clients/${selectedClient!.id}/projects/${selectedProject!.id}`, "clients/:clientId/projects/:projectId");
    }

    return path
  }, [selectedClient, selectedProject]);

  useEffect(() => {
    if (fetched && open) {
      // we'll find info by path
      const info = siteinfos.find((info) => info.path === cleanPath(location.pathname))
      if (info) {
        setLocalLabel(info)
      } else {
        setLocalLabel({
          path: cleanPath(location.pathname),
        })
      }

    }
  }, [fetched, open, location.pathname, siteinfos, setLocalLabel]);


  const submit = useCallback(async () => {
    try {
      const res = await saveSiteinfo(localLabel)
      setSiteinfos(produce(siteinfos, (draft) => {
        const idx = draft.findIndex((d) => d.id === res.id);
        if (idx >= 0) {
          draft[idx] = res;
        } else {
          draft.push(res);
        }
      }))
      showSuccessNotification(`${localLabel.name} Saved`)
      setOpen(false)
    } catch (error) {
      console.error(error)
      showErrorNotification("Failed to save Siteinfo")
    }
    setOpen(false);
  }, [localLabel, showSuccessNotification, showErrorNotification, setOpen, siteinfos, setSiteinfos]);

  return (<>
    {shiftPressed &&
      <Box sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}>
        <Fab color="secondary" onClick={() => {
          setOpen(true);
        }}>
          <TrainIcon style={{ width: 25, height: 25 }} />
        </Fab>
      </Box>
    }
    <StyledDrawer open={open} onClose={() => {
      setOpen(false);
    }}>
      <StyledDrawerTitle>
        <Typography variant="subtitle1">Siteinfo Editor</Typography>
      </StyledDrawerTitle>
      <ValidatorForm onSubmit={submit}>
        <StyledDrawerContainer>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2">Pageinfo for</Typography>
              <Typography variant="body1">{localLabel?.path || ""}</Typography>
            </Box>
            <TextValidator
              label="Name"
              value={localLabel?.name || ""}
              variant="outlined"
              name="name"
              fullWidth
              size="small"
              validators={["required"]}
              errorMessages={["Name is required"]}
              onChange={(e: any) =>
                setLocalLabel((s) =>
                  produce(s, (draft) => {
                    draft!.name = e.target.value;
                  })
                )
              }
            />
            <TextValidator
              label="Description"
              value={localLabel?.description || ""}
              variant="outlined"
              name="description"
              fullWidth
              size="small"
              multiline
              rows={3}
              rowsMax={8}
              validators={["maxStringLength:250"]}
              errorMessages={["Description can only be 250 characters long"]}
              onChange={(e: any) =>
                setLocalLabel((s) =>
                  produce(s, (draft) => {
                    draft!.description = e.target.value;
                  })
                )
              }
            />
          </Stack>
        </StyledDrawerContainer>
        <StyledDrawerActions>
          <Button color="secondary" onClick={() => {
            setOpen(false);
          }}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit">
            Save
          </Button>
        </StyledDrawerActions>
      </ValidatorForm>
    </StyledDrawer>
  </>)
}

export default SiteinfoEditor;